// Shared loader for the tenant's ticket-priority lookup (`/api/config/priorities`).
//
// Tasks now reuse the SAME priority options as tickets (built-in field
// `priority_id` references `ticket_priorities(id)`). Raw task rows from the API
// carry `priority_id` (an id) — never a `priority_code` — so the UI resolves the
// id back to its row here to render a <PriorityBadge code=…/> and selectors.

import { useMemo } from "react";
import { listPriorities, listTaskPriorities } from "./api";
import { useApi } from "./useApi";
import type { TicketPriorityRow, TaskRow } from "./types";

export interface PriorityIndex {
  /** All priority rows, in display order (as returned by the API). */
  list: TicketPriorityRow[];
  /** Resolve a priority id → its row (or undefined). */
  byId: (id: string | null | undefined) => TicketPriorityRow | undefined;
  /** Resolve a priority id → its code (for <PriorityBadge>/badge classes). */
  codeOf: (id: string | null | undefined) => string | undefined;
  loading: boolean;
}

/** Load the tenant priority options once and expose id/code resolvers. */
export function usePriorities(): PriorityIndex {
  const { data, loading } = useApi((signal) => listPriorities(signal), []);
  const list = useMemo<TicketPriorityRow[]>(() => data?.priorities ?? [], [data]);
  return useMemo<PriorityIndex>(() => {
    const map = new Map(list.map((p) => [p.id, p]));
    return {
      list,
      loading,
      byId: (id) => (id ? map.get(id) : undefined),
      codeOf: (id) => (id ? map.get(id)?.code : undefined),
    };
  }, [list, loading]);
}

/** Load the tenant's ACTION-ITEM / project-task priority options (0108 — a set
 *  SEPARATE from ticket priorities). Same resolver shape as usePriorities. */
export function useTaskPriorities(): PriorityIndex {
  const { data, loading } = useApi((signal) => listTaskPriorities(signal), []);
  const list = useMemo<TicketPriorityRow[]>(() => data?.priorities ?? [], [data]);
  return useMemo<PriorityIndex>(() => {
    const map = new Map(list.map((p) => [p.id, p]));
    return {
      list,
      loading,
      byId: (id) => (id ? map.get(id) : undefined),
      codeOf: (id) => (id ? map.get(id)?.code : undefined),
    };
  }, [list, loading]);
}

/** Best-effort priority code for a task: prefer an enriched code, else resolve
 *  the task's priority_id through the loaded index. */
export function taskPriorityCode(task: TaskRow, index: PriorityIndex): string | undefined {
  return task.priority_code ?? index.codeOf(task.priority_id);
}
